import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import Swal from "sweetalert2";
import { showModal, installModalFallback } from "./modalHelper";
import ProjetFormModal, { PROJET_MODAL_ID } from "./ProjetFormModal";
import ProjetGalerie from "./ProjetGalerie";
import ProjetFinances from "./ProjetFinances";
import ProjetPlanning from "./ProjetPlanning";
import ProjetJournal from "./ProjetJournal";
import ProjetDocumentsCommerciaux from "./ProjetDocumentsCommerciaux";
import useReferentiels from "./useReferentiels";
import {
    API, ROUTES, findType, findStatut, findPriorite, findEtape, unwrap, apiMessage, toast,
    formatDate, formatAmount, TypeBadge, StatutBadge, ProgressBar,
} from "./projetsCommon";
import "./GestionProjets.css";

const TABS = [
    { key: "synthese", label: "Synthèse", icon: "fa-tachometer-alt" },
    { key: "equipe", label: "Équipe", icon: "fa-users" },
    { key: "galerie", label: "Galerie", icon: "fa-images" },
    { key: "taches", label: "Phases & tâches", icon: "fa-tasks" },
    { key: "budget", label: "Budget & dépenses", icon: "fa-coins" },
    { key: "commercial", label: "Documents commerciaux", icon: "fa-file-invoice-dollar" },
    { key: "journal", label: "Journal", icon: "fa-book" },
];

const Info = ({ label, children }) => (
    <div className="gp-info">
        <div className="gp-info-label">{label}</div>
        <div className="gp-info-value">{children || "—"}</div>
    </div>
);

const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);

// ---------- Onglet Synthèse ----------
const Synthese = ({ p }) => {
    const type = findType(p.type).value;
    const etape = findEtape(p.etapeVente);
    const today = new Date().toISOString().substring(0, 10);
    const joursRestants = p.dateFin ? daysBetween(today, p.dateFin) : null;
    const duree = p.dateDebut && p.dateFin ? daysBetween(p.dateDebut, p.dateFin) : null;

    return (
        <div className="row">
            <div className="col-lg-8 mb-3">
                <div className="card h-100">
                    <div className="card-header py-2"><strong>Informations</strong></div>
                    <div className="card-body">
                        <div className="gp-info-grid">
                            <Info label="Client">{p.clientName}</Info>
                            <Info label="Responsable">{p.responsableNom}</Info>
                            <Info label="Priorité">{findPriorite(p.priorite).label}</Info>
                            <Info label="Lieu">{p.lieu}</Info>
                            {type === 1 && <Info label="Adresse">{[p.adresse, p.ville].filter(Boolean).join(", ")}</Info>}
                            {type === 2 && <Info label="Étape de vente">{etape?.label}</Info>}
                            {type === 2 && <Info label="Montant estimé">{p.montantEstime != null ? formatAmount(p.montantEstime) : null}</Info>}
                            {type === 2 && <Info label="Probabilité">{p.probabilite != null ? `${p.probabilite} %` : null}</Info>}
                            <Info label="Début prévu / réel">{formatDate(p.dateDebut)} / {formatDate(p.dateDebutReelle)}</Info>
                            <Info label="Fin prévue / réelle">{formatDate(p.dateFin)} / {formatDate(p.dateFinReelle)}</Info>
                            <Info label="Créé le">{formatDate(p.createdAt)}</Info>
                        </div>
                        {p.description && (
                            <>
                                <hr />
                                <div className="gp-info-label">Description</div>
                                <p className="mb-0" style={{ whiteSpace: "pre-line" }}>{p.description}</p>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <div className="col-lg-4 mb-3">
                <div className="card mb-3">
                    <div className="card-body">
                        <div className="gp-info-label mb-1">Avancement</div>
                        <ProgressBar value={p.avancement} enRetard={p.enRetard} />
                        <div className="d-flex justify-content-between mt-3">
                            <div>
                                <div className="gp-info-label">Budget</div>
                                <div className="h5 mb-0">{formatAmount(p.budget)}</div>
                            </div>
                            <div className="text-right">
                                <div className="gp-info-label">Durée prévue</div>
                                <div className="h5 mb-0">{duree != null ? `${duree} j` : "—"}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={`card ${p.enRetard ? "border-danger" : ""}`}>
                    <div className="card-body">
                        <div className="gp-info-label">Échéance</div>
                        {joursRestants == null ? <div className="text-muted">Pas de date de fin</div>
                            : p.enRetard ? <div className="h5 text-danger mb-0"><i className="fas fa-exclamation-triangle mr-1"></i>En retard de {-joursRestants} j</div>
                                : [2, 6].includes(findStatut(p.statut).value) ? <div className="h5 mb-0">{findStatut(p.statut).label}</div>
                                    : <div className="h5 mb-0">{joursRestants} jour(s) restant(s)</div>}
                        <div className="small text-muted mt-2">
                            <i className="fas fa-users mr-1"></i>{p.nbMembres} membre(s) ·
                            <i className="fas fa-paperclip ml-2 mr-1"></i>{p.nbDocuments} document(s)
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ---------- Onglet Équipe ----------
const Equipe = ({ projetId, employes, onChange }) => {
    const [membres, setMembres] = useState([]);
    const [employeId, setEmployeId] = useState("");
    const [role, setRole] = useState("");
    const [saving, setSaving] = useState(false);

    const load = () =>
        axiosInstance.get(API.membres(projetId))
            .then((res) => setMembres(unwrap(res) || []))
            .catch(() => setMembres([]));

    useEffect(() => { load(); }, [projetId]); // eslint-disable-line react-hooks/exhaustive-deps

    const disponibles = employes.filter((e) => !membres.some((m) => m.employeId === e.id));

    const add = async () => {
        if (!employeId) return;
        setSaving(true);
        try {
            await axiosInstance.post(API.membres(projetId), { employeId: Number(employeId), role: role.trim() || null });
            setEmployeId(""); setRole("");
            toast("success", "Membre ajouté");
            load(); onChange();
        } catch (error) {
            toast("error", apiMessage(error, "Ajout impossible"));
        } finally { setSaving(false); }
    };

    const remove = (m) => {
        Swal.fire({
            title: `Retirer ${m.nomComplet} de l'équipe ?`, icon: "warning",
            showCancelButton: true, confirmButtonText: "Retirer", cancelButtonText: "Annuler",
        }).then(async (r) => {
            if (!r.isConfirmed) return;
            try {
                await axiosInstance.delete(API.membre(projetId, m.employeId));
                toast("success", "Membre retiré");
                load(); onChange();
            } catch (error) { toast("error", apiMessage(error, "Opération échouée")); }
        });
    };

    return (
        <div className="card">
            <div className="card-body">
                <div className="form-row align-items-end mb-3">
                    <div className="col-md-5 mb-2">
                        <label className="small mb-1">Employé</label>
                        <select className="form-control" value={employeId} onChange={(e) => setEmployeId(e.target.value)}>
                            <option value="">— Ajouter un membre —</option>
                            {disponibles.map((e) => <option key={e.id} value={e.id}>{e.nomComplet}{e.poste ? ` — ${e.poste}` : ""}</option>)}
                        </select>
                    </div>
                    <div className="col-md-4 mb-2">
                        <label className="small mb-1">Rôle sur le projet</label>
                        <input className="form-control" placeholder="ex : Chef d'équipe" value={role} onChange={(e) => setRole(e.target.value)} />
                    </div>
                    <div className="col-md-3 mb-2">
                        <button className="btn btn-primary btn-block" onClick={add} disabled={!employeId || saving}>
                            <i className="fas fa-user-plus mr-1"></i>Ajouter
                        </button>
                    </div>
                </div>
                {employes.length === 0 && (
                    <p className="small text-muted">Aucun employé actif : créez-en dans Gestion de projets → Employés.</p>
                )}
                <table className="table table-sm mb-0">
                    <thead className="thead-light">
                        <tr><th>Nom</th><th>Poste</th><th>Rôle</th><th>Téléphone</th><th>Depuis</th><th></th></tr>
                    </thead>
                    <tbody>
                        {membres.length === 0 && <tr><td colSpan="6" className="text-center text-muted py-3">Aucun membre</td></tr>}
                        {membres.map((m) => (
                            <tr key={m.employeId}>
                                <td><i className="fas fa-user-circle text-muted mr-1"></i>{m.nomComplet}</td>
                                <td>{m.poste || "—"}</td>
                                <td>{m.role || "—"}</td>
                                <td>{m.telephone || "—"}</td>
                                <td className="small">{formatDate(m.dateAjout)}</td>
                                <td className="text-right">
                                    <button className="btn btn-sm btn-outline-danger" title="Retirer" onClick={() => remove(m)}>
                                        <i className="fas fa-user-minus"></i>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ---------- Page ----------
const ProjetDetail = () => {
    const { id } = useParams();
    const [projet, setProjet] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [tab, setTab] = useState("synthese");
    const [openKey, setOpenKey] = useState(0);
    const { clients, employes } = useReferentiels();

    useEffect(() => installModalFallback(), []);
    useEffect(() => { load(); setTab("synthese"); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const load = () =>
        axiosInstance.get(API.projet(id))
            .then((res) => { setProjet(unwrap(res)); setNotFound(false); })
            .catch((error) => {
                if (error.response?.status === 404) setNotFound(true);
                else toast("error", apiMessage(error, "Impossible de charger le projet"));
            });

    // Le PUT attend le projet complet : on renvoie les valeurs actuelles avec la nouvelle couverture
    const setCover = async (documentId) => {
        const p = projet;
        try {
            await axiosInstance.put(API.projet(p.id), {
                code: p.code, nom: p.nom, type: findType(p.type).value, priorite: findPriorite(p.priorite).value,
                statut: findStatut(p.statut).value, clientId: p.clientId, responsableId: p.responsableId,
                dateDebut: p.dateDebut, dateFin: p.dateFin, dateDebutReelle: p.dateDebutReelle, dateFinReelle: p.dateFinReelle,
                avancement: p.avancement, budget: p.budget, description: p.description,
                lieu: p.lieu, adresse: p.adresse, ville: p.ville,
                montantEstime: p.montantEstime, probabilite: p.probabilite, etapeVente: findEtape(p.etapeVente)?.value ?? null,
                coverDocumentId: documentId,
            });
            toast("success", "Photo de couverture définie");
            load();
        } catch (error) { toast("error", apiMessage(error, "Opération échouée")); }
    };

    if (notFound) return (
        <div className="container-fluid mt-4 text-center text-muted py-5">
            <i className="fas fa-search fa-3x mb-3 d-block"></i>Projet introuvable.
            <div className="mt-3"><Link to={ROUTES.liste}>Retour à la liste</Link></div>
        </div>
    );
    if (!projet) return <div className="container-fluid mt-4"><p>Chargement...</p></div>;

    return (
        <div className="container-fluid mt-4">
            <nav className="small mb-2">
                <Link to={ROUTES.dashboard}>Gestion de projets</Link> / <Link to={ROUTES.liste}>Projets</Link> / {projet.nom}
            </nav>

            <div className="gp-header card mb-3">
                <div className="card-body d-flex flex-wrap align-items-center">
                    <div className="flex-grow-1">
                        <h4 className="mb-1">
                            {projet.code && <span className="text-muted mr-2">{projet.code}</span>}{projet.nom}
                        </h4>
                        <div>
                            <TypeBadge type={projet.type} /> <StatutBadge statut={projet.statut} />
                            {projet.enRetard && <span className="badge badge-danger ml-1"><i className="fas fa-exclamation-triangle mr-1"></i>En retard</span>}
                        </div>
                    </div>
                    <button className="btn btn-outline-secondary" onClick={() => { setOpenKey((k) => k + 1); showModal(PROJET_MODAL_ID); }}>
                        <i className="fas fa-edit mr-1"></i>Modifier
                    </button>
                </div>
            </div>

            <ul className="nav nav-tabs gp-tabs mb-3">
                {TABS.map((t) => (
                    <li key={t.key} className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${tab === t.key ? "active" : ""} ${t.lot ? "disabled" : ""}`}
                            disabled={!!t.lot}
                            title={t.lot ? `Disponible au lot ${t.lot}` : ""}
                            onClick={() => setTab(t.key)}
                        >
                            <i className={`fas ${t.icon} mr-1`}></i>{t.label}
                            {t.lot && <span className="badge badge-light ml-1">bientôt</span>}
                        </button>
                    </li>
                ))}
            </ul>

            {tab === "synthese" && <Synthese p={projet} />}
            {tab === "equipe" && <Equipe projetId={projet.id} employes={employes} onChange={load} />}
            {tab === "taches" && <ProjetPlanning projetId={projet.id} employes={employes} onAvancementChange={load} />}
            {tab === "journal" && <ProjetJournal projetId={projet.id} employes={employes} />}
            {tab === "budget" && <ProjetFinances projet={projet} />}
            {tab === "commercial" && <ProjetDocumentsCommerciaux projetId={projet.id} />}
            {tab === "galerie" && <div className="card"><div className="card-body"><ProjetGalerie projet={projet} onCoverChange={setCover} /></div></div>}

            <ProjetFormModal projet={projet} openKey={openKey} clients={clients} employes={employes} onSaved={load} />
        </div>
    );
};

export default ProjetDetail;
